import { CdkScrollable } from '@angular/cdk/overlay';
import { Component, EventEmitter, HostListener, OnDestroy, OnInit, Output, ViewChild, ViewEncapsulation }
	from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Router, RouterOutlet } from '@angular/router';
import moment from 'moment';
import { firstValueFrom, Subscription } from 'rxjs';
import { TranslateModule } from '@ngx-translate/core';
import { MatButtonModule } from '@angular/material/button';
import { NgIf } from '@angular/common';

import { Batch } from '../../../order/models/batch';
import { pageTransitions } from '../../../shared/animations/page-transitions';
import { DialogOkCancelComponent } from '../../../shared/components/dialog-ok-cancel/dialog-ok-cancel.component';
import { StepClickedEvent, StepStatus, StepperComponent } from '../../../shared/components/stepper/stepper.component';
import { User } from '../../../shared/models/user';
import { LocationService } from '../../../shared/services/location.service';
import { TranslationService } from '../../../shared/services/translation.service';
import { UserService } from '../../../shared/services/user.service';
import { CalledBatch, EcallOffPageContent, EcallResponse, RDD_MODE } from '../../models/ecall-off-model';
import { ECallOffService } from '../../services/ecall-off.service';
import { AfterViewInitDirective } from '../../../shared/directives/after-view-init.directive';
import { LoadingSpinnerComponent } from '../../../shared/components/utils/loading-spinner.component';
import { PageHeaderComponent } from '../../../shared/components/page-header/page-header.component';
import { ScrollerDirective } from '../../../shared/directives/scroller.directive';
import { SnackbarSaveEcallOffComponent } from '../utils/snackbar-save-ecall-off/snackbar-save-ecall-off.component';
import { EcallOffShipToDetails } from '../../../order/models/ecall-off-ship-to-details';

const DATE_FORMAT = 'YYYY-MM-DD';

@Component({
	selector: 'copo-ecall-off-steps',
	templateUrl: './ecall-off-steps.component.html',
	styleUrl: './ecall-off-steps.component.css',
	animations: pageTransitions,
	encapsulation: ViewEncapsulation.Emulated,
	imports: [
		ScrollerDirective,
		PageHeaderComponent,
		NgIf,

		MatButtonModule,
		TranslateModule,
		StepperComponent,
		LoadingSpinnerComponent,
		RouterOutlet,
		AfterViewInitDirective,
	],
})
export class EcallOffStepsComponent implements OnDestroy, OnInit {
	@ViewChild('scroller', { read: CdkScrollable }) scrollBar?: CdkScrollable;
	@Output() readonly selectShipTo = new EventEmitter<string>();
	pageContent: EcallOffPageContent | undefined;
	isLoading = false;
	isSaving = false;
	hideNextBtn = false;
	enableNext = false;
	pageTitle: string;
	calledBatches: CalledBatch[] = [];
	shipToDetails?: EcallOffShipToDetails[];
	allTransactionalShipToDetails?: EcallOffShipToDetails[];
	selectedBatches: Batch[] = [];
	custCallOffRef?: string;
	rddMode?: string;
	copyTo?: string;
	sub?: Subscription;
	ecallSub?: Subscription;
	selectedBatchesSubscription?: Subscription;
	user?: User;
	readonly steps;

	rddModes = RDD_MODE;
	private readonly pageTitleTranslationCode = 'ecall-off.call-off-request';

	constructor(
		private readonly translationService: TranslationService,
		private readonly router: Router,
		private readonly route: ActivatedRoute,
		private readonly ecallOffService: ECallOffService,
		private readonly locationService: LocationService,
		private readonly userService: UserService,
		private readonly dialog: MatDialog,
		private readonly snackBar: MatSnackBar,
	) {
		this.steps = [
			{
				label: this.translationService.getTR('ecall-off.material-picking'),
				status: StepStatus.CURRENT,
			},
			{
				label: this.translationService.getTR('ecall-off.information-entry'),
				status: StepStatus.DISABLED,
			},
			{
				label: this.translationService.getTR('ecall-off.summary'),
				status: StepStatus.DISABLED,
			}
		];
		this.selectedBatchesSubscription = this.ecallOffService.currentSelectedBatchesStep1
			.subscribe((data: Batch[]) => {
				this.selectedBatches = data;
			});
		this.pageTitle = this.translationService.getTR(this.pageTitleTranslationCode);
		if (!this.ecallOffService.consumeBatchCardRedirect()) {
			this.clearAll();
		}
		if (this.ecallOffService.getCurrentRoute().endsWith('ecall-off-steps/stepInfoEntry')
			&& this.selectedBatches.length === 0) {
			this.locationService.go(-2, '/ecall-off/ecall-off-steps/stepMatPicking');
		}
		this.user = this.userService.user;
	}

	@HostListener('window:beforeunload') goToPage() {
		if (this.ecallOffService.getCurrentRoute().endsWith('ecall-off-steps/stepSummary')) {
			this.locationService.go(-2, '/ecall-off/ecall-off-steps/stepMatPicking');
		} else if (this.ecallOffService.getCurrentRoute().endsWith('ecall-off-steps/stepInfoEntry')) {
			this.locationService.go(-1, '/ecall-off/ecall-off-steps/stepMatPicking');
		}
		this.clearAll();
	}

	goBack() {
		this.locationService.go(-1, '/home/order-services');
		this.clearAll();
	}

	async goBackOrOpenDialog() {
		if (this.selectedBatches.length === 0) {
			this.goBack();
			return;
		}
		if (this.steps.findIndex(step => step.status === StepStatus.CURRENT) === 0) {
			const dialogRef = this.dialog.open(DialogOkCancelComponent, {
				data: { title: 'quapp.action.go.back', content: 'dialog.loose-data-message' },
			});
			firstValueFrom(dialogRef.afterClosed())
				.then(result => {
					if (result) {
						this.goBack();
					}
				})
				.catch((error: unknown) => {
					console.error('Error during go back dialog', error);
				});
		}
		if (this.steps.findIndex(step => step.status === StepStatus.CURRENT) === 1
			&& this.selectedBatches.length > 0) {
			this.steps[1].status = StepStatus.DISABLED;
			this.steps[2].status = StepStatus.DISABLED;
			this.steps[0].status = StepStatus.CURRENT;
			await this.router.navigate(['/ecall-off/ecall-off-steps/stepMatPicking'], { relativeTo: this.route });
			this.hideNextBtn = false;
		}
		if (this.steps.findIndex(step => step.status === StepStatus.CURRENT) === 2
			&& this.selectedBatches.length > 0) {
			this.steps[1].status = StepStatus.CURRENT;
			this.steps[2].status = StepStatus.DISABLED;
			this.steps[0].status = StepStatus.ENABLED;
			await this.router.navigate(['/ecall-off/ecall-off-steps/stepInfoEntry'], { relativeTo: this.route });
			this.hideNextBtn = false;
		}
	}

	clearAll() {
		this.ecallOffService.updateCopyTo('');
		this.ecallOffService.updateCustCallOffRef('');
		this.ecallOffService.updateRddMode('ASAP');
		this.ecallOffService.updateSelectedBatchesStep2(<CalledBatch[]>[]);
		this.ecallOffService.updateMills(undefined);
		this.ecallOffService.setSelectedBatches(<Batch[]>[]);
		this.ecallOffService.updateUnloadingRef('');
		this.ecallOffService.updateSelectedShipto(<EcallOffShipToDetails>{});
	}

	async onStepClicked(event: StepClickedEvent) {
		this.steps[event.stepIndex].status = StepStatus.CURRENT;
		if (event.stepIndex === 2) {
			this.steps[event.stepIndex - 1].status = StepStatus.ENABLED;
			this.steps[event.stepIndex - 2].status = StepStatus.ENABLED;
			await this.router.navigate(['/ecall-off/ecall-off-steps/stepSummary'], { relativeTo: this.route });
			this.hideNextBtn = true;
		} else if (event.stepIndex === 1) {
			this.steps[event.stepIndex + 1].status = StepStatus.DISABLED;
			this.steps[event.stepIndex - 1].status = StepStatus.ENABLED;
			await this.router.navigate(['/ecall-off/ecall-off-steps/stepInfoEntry'], { relativeTo: this.route });
			this.hideNextBtn = false;
		} else {
			this.steps[event.stepIndex + 1].status = StepStatus.DISABLED;
			this.steps[event.stepIndex + 2].status = StepStatus.DISABLED;
			await this.router.navigate(['/ecall-off/ecall-off-steps/stepMatPicking'], { relativeTo: this.route });
			this.hideNextBtn = false;
		}
	}

	onActivate(pageContent: EcallOffPageContent) {
		this.pageContent = pageContent;
	}

	async goToNext() {
		this.sub = this.ecallOffService.currentCopyTo
			.subscribe(copyTo => {
				this.copyTo = copyTo;
			});
		this.sub = this.ecallOffService.currentRddMode
			.subscribe(rddMode => {
				this.rddMode = rddMode;
			});
		this.sub = this.ecallOffService.currentCustCallOffRef
			.subscribe(custCallOffRef => {
				this.custCallOffRef = custCallOffRef;
			});
		this.sub = this.ecallOffService.currentselectedBatchesStep2
			.subscribe(batches => {
				this.calledBatches = batches;
			});
		this.sub = this.ecallOffService.shipToList
			.subscribe(shipTos => {
				this.shipToDetails = shipTos;
			});
		if (this.steps.findIndex(step => step.status === StepStatus.CURRENT) === 0
			&& this.selectedBatches.length > 0) {
			this.steps[1].status = StepStatus.CURRENT;
			this.steps[2].status = StepStatus.DISABLED;
			this.steps[0].status = StepStatus.ENABLED;
			await this.router.navigate(['/ecall-off/ecall-off-steps/stepInfoEntry'], { relativeTo: this.route });
		} else if (this.steps.findIndex(step => step.status === StepStatus.CURRENT) === 1
		) {
			this.pageContent?.checkErrors();
			if (this.rddMode === 'EXACT') {
				const isValidCombination = this.checkMillAndShipToCombination();
				if (!isValidCombination) {
					return;
				}
			}
			if (this.rddMode && this.custCallOffRef	&& this.validate() && this.pageContent?.checkErrors()) {
				this.steps[1].status = StepStatus.ENABLED;
				this.steps[2].status = StepStatus.CURRENT;
				this.steps[0].status = StepStatus.ENABLED;
				await this.router.navigate(['/ecall-off/ecall-off-steps/stepSummary'], { relativeTo: this.route });
				this.hideNextBtn = true;
			}
		}
	}

	validate() {
		switch (this.rddMode) {
		case 'EARLIEST':
			return this.calledBatches.every(
				batch =>
					'requestedDeliveryDateFrom' in batch
					&& batch.requestedDeliveryDateFrom
					&& batch.requestedDeliveryDateFrom !== ''
			);
		case 'EXACT':
		case 'BETWEEN':
			return this.calledBatches.every(
				batch =>
					'requestedDeliveryDateFrom' in batch
					&& batch.requestedDeliveryDateFrom
					&& batch.requestedDeliveryDateFrom !== ''
					&& 'requestedDeliveryDateTo' in batch
					&& batch.requestedDeliveryDateTo
					&& batch.requestedDeliveryDateTo !== ''
			);
		case 'LATEST':
			return this.calledBatches.every(
				batch =>
					'requestedDeliveryDateTo' in batch
					&& batch.requestedDeliveryDateTo
					&& batch.requestedDeliveryDateTo !== ''
			);
		default:
			return true;
		}
	}

	isButtonSubmitVisible() {
		return !this.isLoading && this.steps.findIndex(step => step.status === StepStatus.CURRENT) === 2;
	}

	saveEcall() {
		if (this.isSaving) {
			return;
		}
		this.isSaving = true;
		const requestBody: EcallResponse = {
			type: 'B',
			customerReference: this.custCallOffRef,
			copyTo: this.copyTo,
			rddMode: this.rddMode,
			ecallBatches: this.calledBatches.map(ecallBatch => {
				const dateFrom = ecallBatch.requestedDeliveryDateFrom
					? moment(ecallBatch.requestedDeliveryDateFrom).format(DATE_FORMAT) : undefined;
				const dateTo = ecallBatch.requestedDeliveryDateTo
					? moment(ecallBatch.requestedDeliveryDateTo).format(DATE_FORMAT) : undefined;
				// eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
				const shipTo = ecallBatch.shipTo !== null && ecallBatch.shipTo !== ''
					&& ecallBatch.shipTo !== undefined ? ecallBatch.shipTo : ecallBatch.originalShipTo;
				// eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
				const shipToDescription = ecallBatch.shipToDescription !== null
					&& ecallBatch.shipToDescription !== '' && ecallBatch.shipToDescription !== undefined
					? ecallBatch.shipToDescription : ecallBatch.originalShipToDescription;

				return <CalledBatch> {
					...ecallBatch,
					shipTo,
					shipToDescription,
					requestedDeliveryDateFrom: dateFrom,
					requestedDeliveryDateTo: dateTo
				};
			})
		};
		this.ecallSub = this.ecallOffService.saveEcall(requestBody)
			.subscribe({
				next: async result => {
					this.isSaving = false;
					requestBody.id = result;
					this.snackBar.openFromComponent(
						SnackbarSaveEcallOffComponent,
						{
							duration: 5000,
							panelClass: ['copo-claim-draft-snackbar'],
							data: requestBody.customerReference
						}
					);
					await this.goToEcallsPage();
				},
				error: (error: unknown) => {
					this.isSaving = false;
					console.error(error);
				}
			});
	}

	async goToEcallsPage() {
		this.clearAll();
		await this.router.navigate(['/ecall-off/ecall/list/']);
	}

	ngOnInit() {
		const loginUser = this.user;
		const currentVisibleOrgId = loginUser?.currentVisibility.organizationId;
		if (!currentVisibleOrgId) {
			return;
		}
		this.getShipToValue(currentVisibleOrgId).catch((err: unknown) => {
			console.error(err);
		});
	}

	ngOnDestroy() {
		if (this.sub !== undefined) {
			this.sub.unsubscribe();
		}
		if (this.ecallSub !== undefined) {
			this.ecallSub.unsubscribe();
		}
	}

	async getShipToValue(id: number) {
		try {
			await this.ecallOffService.getShipToDetails(id).then(shipTos => {
				if (this.hasExactModeAvailable(shipTos) && !this.rddModes.includes('EXACT')) {
					this.rddModes.push('EXACT');
				}
				const lang = this.translationService.getLangWithRegion();
				const regionNames = new Intl.DisplayNames([lang], { type: 'region' });
				for (const shipTo of shipTos) {
					const countryName = shipTo.country ? regionNames.of(shipTo.country) : undefined;
					if (countryName) {
						shipTo.country = countryName;
					}
				}
				this.allTransactionalShipToDetails = shipTos;
				this.shipToDetails = shipTos.filter(shipto => shipto.allowChangeOfShipto);
				this.ecallOffService.updateSelectedShipTos(this.shipToDetails);
			});
		} catch (err) {
			console.error('Error from http get current shipTo details', err);
		}
	}

	hasExactModeAvailable(shipTos: EcallOffShipToDetails[]): boolean {
		return shipTos.some(shipTo => shipTo.millCodes?.some(Boolean)) || false;
	}

	private checkMillAndShipToCombination() {
		for (const batch of this.calledBatches) {
			batch.shipTo = batch.shipTo ? batch.shipTo.replace(/^0+/, '') : batch.originalShipTo
				? batch.originalShipTo.replace(/^0+/, '') : batch.originalShipTo;
			const currentShipToDetails = this.allTransactionalShipToDetails?.find(shipto => shipto.shipTo
				=== batch.shipTo);
			const isValid = this.ecallOffService.validateMillAndShipToCombination(
				currentShipToDetails,
				batch.millCode
			);
			if (!isValid) {
				this.snackBar.open(
					this.translationService.getTR('ecall.exct-date-error'),
					undefined,
					{ duration: 5000 }
				);
				return false;
			}
		}
		return true;
	}
}
